import { BrowserRouter } from 'react-router-dom';
import { useState } from 'react';
import { AppRoutes } from './router';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { AppDataProvider, useAppData } from './contexts/AppDataContext';

function MaintenancePage() {
  const { siteSettings } = useAppData();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl"
            style={{ 
              background: `linear-gradient(135deg, ${siteSettings.primaryColor}, ${siteSettings.accentColor})` 
            }}
          >
            <i className={`fas ${siteSettings.logoIcon} text-4xl text-white`}></i>
          </div>
        </div>

        {/* Main Message */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-12 mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-tools-line text-3xl text-amber-600"></i>
          </div>
          
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Site Under Maintenance
          </h1>
          
          <p className="text-lg text-slate-600 mb-6">
            We're currently performing scheduled maintenance to improve your experience.
          </p>
          
          <p className="text-slate-500 mb-8">
            Our team is working hard to get everything back online as soon as possible. 
            We apologize for any inconvenience this may cause.
          </p>

          {/* Contact Info */}
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-3">
              Need immediate assistance?
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
              <i className="ri-mail-line" style={{ color: siteSettings.primaryColor }}></i>
              <a 
                href={`mailto:${siteSettings.siteEmail}`}
                className="font-medium hover:underline"
                style={{ color: siteSettings.primaryColor }}
              >
                {siteSettings.siteEmail}
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-sm text-slate-400">
          © {new Date().getFullYear()} {siteSettings.siteName}. All rights reserved.
        </p>
      </div>
    </div>
  );
}

function AnnouncementBanner() {
  const { siteSettings } = useAppData();
  const [dismissed, setDismissed] = useState(false);

  if (!siteSettings.announcementBanner.enabled || dismissed) {
    return null;
  }

  return (
    <div 
      className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between gap-4"
      style={{ 
        backgroundColor: siteSettings.announcementBanner.bgColor,
        color: siteSettings.announcementBanner.textColor
      }}
    >
      <div className="flex items-center gap-3 flex-1 max-w-7xl mx-auto">
        <i className="ri-information-line text-xl flex-shrink-0"></i>
        <p className="text-sm font-medium">
          {siteSettings.announcementBanner.message}
        </p>
      </div>
      <button 
        onClick={() => setDismissed(true)}
        className="flex-shrink-0 hover:opacity-70 transition-opacity cursor-pointer w-8 h-8 flex items-center justify-center"
        aria-label="Dismiss announcement"
      >
        <i className="ri-close-line text-xl"></i>
      </button>
    </div>
  );
}

function AppContent() {
  const { isAdmin, siteSettings } = useAppData();

  // Show maintenance page to non-admin users when maintenance mode is ON
  if (siteSettings.maintenanceMode && !isAdmin) {
    return <MaintenancePage />;
  }

  return (
    <>
      <AnnouncementBanner />
      <AppRoutes />
    </>
  );
}

function App() {
  return (
    <CurrencyProvider>
      <AppDataProvider>
        <BrowserRouter basename={__BASE_PATH__}>
          <AppContent />
        </BrowserRouter>
      </AppDataProvider>
    </CurrencyProvider>
  );
}

export default App;